import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { HttpStatusEnum, UserApi } from '@core/api';
import { getRouteQueryParams, getRouteWithoutQueryParamsAndGroupId, missingGroupFromUrl, noGroup } from '@core/helpers';
import { TranslateService } from '@ngx-translate/core';
import { UserRoleEnum } from '@shared/enums';
import { AppRoutes, ToasterHelper } from '@shared/helpers';
import { UserGroup } from '@shared/interfaces';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, filter, firstValueFrom, Subject, tap } from 'rxjs';
import { GroupsApi } from 'src/app/features/groups/_core/api';
import { CurrentUserService } from './current-user.service';

@Injectable({ providedIn: 'root' })
export class UserGroupService {
    readonly currentGroup$ = new BehaviorSubject<UserGroup | null>(null);
    readonly groups$ = new BehaviorSubject<UserGroup[] | null>(null);
    readonly switchGroupGuard$ = new BehaviorSubject<'normal' | 'ready' | 'wait' | 'cancel'>('normal');
    readonly displaySaveChangesModal$ = new Subject<void>();

    readonly groupsError$ = new Subject<void>();

    private loadingGroups$ = new BehaviorSubject<'loading' | 'ready'>('ready');

    constructor(
        private router: Router,
        private userApi: UserApi,
        private location: Location,
        private messageService: MessageService,
        private translateService: TranslateService,
        private currentUserService: CurrentUserService,
        private groupsApi: GroupsApi
    ) {}

    setGroups(groups: UserGroup[] | null) {
        this.groups$.next(groups);
    }

    private setCurrentGroup(group: UserGroup) {
        if (this.currentUserService.currentUser$.value?.role === UserRoleEnum.USER) {
            const currentGroups = this.groups$.value;
            if (currentGroups && group.id !== noGroup.id) {
                const searchedGroup = currentGroups.find(menuGroup => menuGroup.id === group.id);
                if (!searchedGroup) this.groups$.next([...currentGroups, group]);
            }
        }

        AppRoutes.updateGroupId(group.id);
        this.currentGroup$.next(group);
    }

    setCurrentGroupName(name: string) {
        const currentGroup = this.currentGroup$.value;

        if (currentGroup) {
            currentGroup.name = name;
            this.currentGroup$.next(currentGroup);
        }
    }

    triggerGroupsError(): void {
        this.setGroups(null);
        this.groupsError$.next();
    }

    async switchGroup(
        group: UserGroup,
        configs: { navigateTo: 'dashboard' | 'groups-management' | 'keep-current-route' }
    ) {
        this.setCurrentGroup(group);

        if (configs.navigateTo === 'dashboard') this.router.navigateByUrl(AppRoutes.routes().DASHBOARD);
        if (configs.navigateTo === 'groups-management') this.router.navigateByUrl(AppRoutes.routes().GROUPS_LISTING);
        if (configs.navigateTo === 'keep-current-route') {
            this.router.navigate(
                [`/${AppRoutes.seqGroupId}${getRouteWithoutQueryParamsAndGroupId(this.location.path())}`],
                {
                    queryParams: getRouteQueryParams(this.location.path()),
                }
            );
        }

        if (group.id) await this.userApi.switchGroup(group.id);
    }

    removeGroup(groupId: number) {
        if (this.groups$.value?.length) {
            const groups = this.groups$.value;
            const searchedGroupIndex = groups.findIndex(group => groupId === group.id);
            groups.splice(searchedGroupIndex, 1);
            this.groups$.next(groups);
        }
    }

    getDefaultGroup() {
        if (this.groups$.value?.length) {
            return this.groups$.value.find(el => el.isDefault) ?? this.groups$.value[0];
        } else {
            return noGroup;
        }
    }

    async getRedirectUrlTree(groupId: number, currentUrl: string) {
        const selectedGroup = await this.getValidGroup(groupId);

        this.setCurrentGroup(selectedGroup);
        const redirectToRoute = this.getNavigateByGroupUrl(groupId, selectedGroup.id, currentUrl);
        if (redirectToRoute === null) return null;

        return this.router.createUrlTree([redirectToRoute.url], { queryParams: redirectToRoute.queryParams });
    }

    private async recalculateCurrentGroup(groupId: number, currentUrl: string) {
        this.messageService.add(ToasterHelper.error(this.translateService.instant('groups.invalidGroup.error')));

        if (!this.groups$.value?.length) {
            await this.navigateToGroupList(currentUrl);
            return;
        }

        const selectedGroup = await this.getValidGroup(groupId);

        this.setCurrentGroup(selectedGroup);

        this.router.navigate([`/${selectedGroup.id}/${AppRoutes.routesSegments.DASHBOARD}`]);
    }

    async initializeUserGroups() {
        const response = await this.userApi.getUserGroups();

        if (response.status == HttpStatusEnum.ERROR) {
            this.triggerGroupsError();
        }

        if (response.status == HttpStatusEnum.SUCCESS && response.data) {
            this.setGroups(response.data);
        }

        return response;
    }

    private async getValidGroup(groupId: number): Promise<UserGroup> {
        const requestedGroup = this.groups$.value?.find(el => el.id === groupId);
        if (requestedGroup) return requestedGroup;

        if (
            this.currentUserService.currentUser$.value?.role === UserRoleEnum.ADMIN &&
            groupId !== noGroup.id &&
            groupId !== missingGroupFromUrl
        ) {
            const response = await this.groupsApi.getCurrentGroupInfo(groupId);
            if (response.status === HttpStatusEnum.SUCCESS && response.data) return response.data;
        }

        return this.getDefaultGroup();
    }

    private getNavigateByGroupUrl(
        urlGroupId: number,
        selectedGroupId: number,
        currentUrl: string
    ): { url: string; queryParams: Params } | null {
        const route = { urlWithoutId: currentUrl.split('?')[0], queryParams: getRouteQueryParams(currentUrl) };

        if (urlGroupId !== missingGroupFromUrl) route.urlWithoutId = getRouteWithoutQueryParamsAndGroupId(currentUrl);

        if (route.urlWithoutId === '/' || route.urlWithoutId === '') {
            return {
                queryParams: route.queryParams,
                url: `/${selectedGroupId}/${AppRoutes.routesSegments.DASHBOARD}`,
            };
        }

        if (urlGroupId === selectedGroupId) return null;

        if (urlGroupId !== missingGroupFromUrl && urlGroupId !== noGroup.id) {
            this.messageService.add(ToasterHelper.error(this.translateService.instant('groups.invalidGroup.error')));
        }

        return { url: `/${selectedGroupId}${route.urlWithoutId}`, queryParams: route.queryParams };
    }

    getNavigateToGroupListUrlTree(urlGroupId: number, currentUrl: string) {
        this.setCurrentGroup(noGroup);

        // We need this when leaving the last active group you joined
        if (urlGroupId !== noGroup.id) {
            return this.router.createUrlTree(['/']);
        }

        return this.router.createUrlTree([`${AppRoutes.routes().GROUPS_LISTING}`], {
            queryParams: getRouteQueryParams(currentUrl),
        });
    }

    private navigateToGroupList(currentUrl: string) {
        this.setCurrentGroup(noGroup);

        return this.router.navigate([`${AppRoutes.routes().GROUPS_LISTING}`], {
            queryParams: getRouteQueryParams(currentUrl),
        });
    }

    async forcedGroupChange(defaultSelectedGroupId: number) {
        if (this.loadingGroups$.value === 'loading') {
            await firstValueFrom(this.loadingGroups$.pipe(filter(state => state === 'ready')));
            return;
        }

        this.loadingGroups$.next('loading');

        const urlGroupId = AppRoutes.seqGroupId;

        AppRoutes.updateGroupId(defaultSelectedGroupId);
        await this.initializeUserGroups();
        this.recalculateCurrentGroup(urlGroupId, this.location.path());

        this.loadingGroups$.next('ready');
    }

    async getSaveChangesModalResponse(): Promise<string> {
        return await firstValueFrom(
            this.switchGroupGuard$.pipe(
                tap(event => {
                    if (event === 'wait') this.displaySaveChangesModal$.next();
                }),
                filter(event => {
                    return event !== 'wait';
                })
            )
        );
    }
}
